<script setup lang="ts">
import { defineShortcuts } from '@nuxt/ui/composables';
import { storeToRefs } from 'pinia';
import { WorkspaceLogo } from '#layers/assets/public';
import { useSettingsOverlay } from '#layers/global-settings/composables/use-settings-overlay';
import { useWorkspaceStore } from '#layers/workspace/public';

interface Props {
	/** Keeps the workspace mark mounted while disabling launcher actions during context transitions. */
	interactive?: boolean;
}

const { interactive = true } = defineProps<Props>();
const { currentWorkspace } = storeToRefs(useWorkspaceStore());
const { open } = useSettingsOverlay();

defineShortcuts({
	meta_shift_o: () => {
		if (interactive) open('organization/workspaces');
	},
});
</script>

<template>
	<div v-if="currentWorkspace" class="relative grid">
		<Transition
			enter-active-class="transition-opacity duration-150 ease-in-out motion-reduce:transition-none"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-active-class="transition-opacity duration-150 ease-in-out motion-reduce:transition-none"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<WorkspaceLogo
				:key="currentWorkspace.id"
				:workspace="currentWorkspace"
				size="sm"
				class="[grid-area:1/1]"
			/>
		</Transition>

		<DTooltip
			v-if="interactive"
			:text="$t('workspace.switcher.go_to_organization')"
			:content="{ side: 'right', sideOffset: 8 }"
			:kbds="['meta', 'shift', 'O']"
		>
			<button
				type="button"
				data-testid="workspace-badge"
				class="
					group absolute inset-0 rounded-md
					focus-visible:ring-2 focus-visible:ring-(--ui-border-focused) focus-visible:outline-none
				"
				:aria-label="$t('workspace.switcher.go_to_organization')"
				@click="open('organization/workspaces')"
			>
				<div
					data-testid="workspace-badge-hover-logo"
					aria-hidden="true"
					class="
						absolute inset-0 flex items-center justify-center rounded-md bg-(--brand-color,var(--color-primary)) opacity-0
						transition-opacity duration-150 ease-out
						group-hover:opacity-100
						group-focus-visible:opacity-100
					"
					:style="currentWorkspace.primaryColor ? { '--brand-color': currentWorkspace.primaryColor } : undefined"
				>
					<DIcon name="lucide:maximize" class="size-5 text-white" />
				</div>
			</button>
		</DTooltip>
	</div>
</template>
